import React, { useEffect, useCallback, useRef, useState } from 'react';
import { TABLE_ROW_CLASS, TABLE_ROW_HEIGHT } from '../planner-table';
import type { ScrollDirection, VariableSizeList as List } from 'react-window';
import { last as _last, isEmpty as _isEmpty, throttle } from 'lodash';
import type { Row } from 'react-table';
import { GROUP_CLASS_NAME } from '../table-components/grouped-row';
import type { RoutesPlannerTask } from '../../../types';

const getNextSibling = (elem: Element, selector: string) => {
	let sibling = elem.nextElementSibling;

	while (sibling) {
		if (sibling.matches(selector)) return sibling;
		sibling = sibling.nextElementSibling;
	}
};

const areNextGroupedRowAndPreviousRowFromSameGroup = (nextGroupSibling: Element, rows: Row<RoutesPlannerTask>[]) =>
	nextGroupSibling &&
	rows[nextGroupSibling.getAttribute('data-row-index')].groupByVal !==
		nextGroupSibling.previousElementSibling?.getAttribute('data-group-by-value');

export interface IUseStickyRowProps {
	listRef: React.MutableRefObject<List<any>>;
	bodyRef: React.MutableRefObject<HTMLDivElement>;
	groupByState: string[];
	expandedState: Record<string, boolean>;
	rows: Row<RoutesPlannerTask>[];
}

const ROW_THRESHOLD = 10;

const useStickyRow = ({ listRef, rows, groupByState, expandedState, bodyRef }: IUseStickyRowProps) => {
	const indexRowByGroupMap = useRef(new Map<string, number>());
	const [stickyIndex, setStickyIndex] = useState<number>(null);
	const lastGroupId = _last(groupByState);
	const expandSelector = `.${GROUP_CLASS_NAME}[data-group-by=${lastGroupId}][data-row-expanded="true"]:not(.sticky)`;

	useEffect(() => {
		if (_isEmpty(expandedState)) {
			setStickyIndex(null);
		}
	}, [expandedState]);

	const getGroupNextSibling = (groupEl?: Element) => {
		return groupEl
			? getNextSibling(groupEl, `.${GROUP_CLASS_NAME}`)
			: bodyRef.current.querySelector(`.${GROUP_CLASS_NAME}:not(.sticky)`);
	};

	const getIndexFromElement = (element?: Element) => {
		if (element) {
			return Number(element.getAttribute('data-row-index'));
		}

		const rowGroupByValue = bodyRef.current
			.querySelector(`.${TABLE_ROW_CLASS}`)
			.getAttribute('data-group-by-value');

		return rows.findIndex(row => row.groupByVal === rowGroupByValue);
	};

	const setIndexToStickyMap = (index: number) => {
		const { groupByID, groupByVal } = rows[index];
		indexRowByGroupMap.current.set(`${groupByID}:${groupByVal}`, index);
	};

	const getRowIndexFromGroupMap = (groupEl: Element) => {
		const rowAbove = rows[Number(groupEl.getAttribute('data-row-index')) - 1];
		const index = indexRowByGroupMap.current.get(`${lastGroupId}:${rowAbove?.values[lastGroupId]}`);

		return index;
	};

	const calculateSticky = useCallback(
		throttle((scrollDirection: ScrollDirection) => {
			const bodyTop = bodyRef.current.getBoundingClientRect().top;
			const expandedElements = bodyRef.current.querySelectorAll(expandSelector);

			const groupElToStick = Array.from(expandedElements)
				.filter(el => el.getBoundingClientRect().top < bodyTop)
				.pop();

			const nextGroupSibling = getGroupNextSibling(groupElToStick);

			const nextGroupSiblingTop = nextGroupSibling?.getBoundingClientRect().top || 999999;

			const nextGroupIsAboutToScrolledOut = nextGroupSiblingTop - TABLE_ROW_HEIGHT - ROW_THRESHOLD < bodyTop;

			if (scrollDirection === 'forward') {
				if (nextGroupIsAboutToScrolledOut) {
					setStickyIndex(null);
				} else if (nextGroupSiblingTop > bodyTop) {
					const index = getIndexFromElement(groupElToStick);
					setStickyIndex(index);
					setIndexToStickyMap(index);
				}
			}

			if (scrollDirection === 'backward') {
				const groupElToStickIsAboutToEnter =
					groupElToStick?.getBoundingClientRect().top > bodyTop - TABLE_ROW_HEIGHT - ROW_THRESHOLD;
				const isGroupedToStickyEnters =
					stickyIndex === null && nextGroupSiblingTop - TABLE_ROW_HEIGHT > bodyTop;

				if (groupElToStickIsAboutToEnter || nextGroupIsAboutToScrolledOut) {
					setStickyIndex(null);
				} else if (
					isGroupedToStickyEnters ||
					areNextGroupedRowAndPreviousRowFromSameGroup(nextGroupSibling, rows)
				) {
					const index = nextGroupSibling ? getRowIndexFromGroupMap(nextGroupSibling) : null;
					setStickyIndex(index);
				}
			}
		}, 0),
		[rows, groupByState, bodyRef, stickyIndex]
	);

	const onStickyExpand = useCallback(() => {
		listRef.current.resetAfterIndex(stickyIndex);
		listRef.current.scrollToItem(stickyIndex);
		clearSticky();
	}, [stickyIndex]);

	const clearSticky = () => {
		setStickyIndex(null);
	};

	return { stickyIndex, onStickyExpand, calculateSticky, clearSticky };
};

export default useStickyRow;
